import React, { createContext, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'react-apollo';
import PropTypes from 'prop-types';

import CourseOutlineConfig from 'legacy-src/utility/CourseOutlineConfig';
import withSubmissionData from 'legacy-src/utility/withSubmissionData';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import ResultsWrapper from 'legacy-src/components/assessment/Results/ResultsWrapper';
import { isFRQ, isSecure } from 'legacy-src/utility/progressHelpers';
import { BreadcrumbContext } from 'chameleon/ui-stack/ui/basic/breadcrumbs';
import * as types from 'legacy-src/constants/types';
import WithLearnosityData from './utils/withLearnosityData';
import {
  combineTeacherAndStudentScores,
  pathBreadCrumbMapping,
} from './utils/utils';
import {
  withAssignment,
  withTrackResultsVisit,
  withAdminInfo,
  withSessionTemplate,
} from './utils/graphql';
import { isNil } from 'lodash';

export const ResultsContext = createContext();

const ResultsDataProvider = (props) => {
  const {
    resultsByRubric,
    studentScoringResults,
    teacherScoringResults,
    courseOutlineConfig,
    assessment,
    questions,
    isPreApSubject,
    results,
    resultsByItem,
    subjectInfo = {},
    responseAnalysis,
    scoringRubricResponseAnalysis,
    assignmentResultsLoading,
    router,
    params,
    noResults,
    userData: {
      me: { isStudent, initId },
    },
    location: { pathname },
    isReady,
    route,
    prevLocation,
  } = props;

  if (assignmentResultsLoading) {
    return <LoadingSpinner />;
  }

  const hasFrqQuestions = questions && !!questions.find(isFRQ);
  const { type } = subjectInfo;
  const isQB = type && type.toUpperCase() === types.QuestionBank;
  const hasFrqNonSecureQuestions =
    questions &&
    !!questions.find((question) => isFRQ(question) && !isSecure(question));

  const combinedRubricResults = combineTeacherAndStudentScores(
    resultsByRubric,
    studentScoringResults,
  );
  const { curriculumCheckTabLabel: resourcesTabLabel, ppcTagPair } =
    courseOutlineConfig.data || {};
  const { libraryType } = assessment || {};
  const currentStudentId = isStudent ? initId : params.studentId || initId;

  const isPPC =
    !hasFrqQuestions &&
    resourcesTabLabel &&
    ppcTagPair &&
    !isPreApSubject &&
    libraryType === 'FYM';

  const resultsData = {
    ...props,
    isQB,
    isPPC,
    currentStudentId,
    isStudent,
    studentScoringResults,
    teacherScoringResults,
    isPreApSubject,
    hasFrqQuestions,
    resourcesTabLabel,
    hasFrqNonSecureQuestions,
    me: props.userData,
    learnosityReportsData: results,
    learnosityReportsByItemData: resultsByItem,
    learnosityReportsByRubricData: combinedRubricResults,
    learnosityReportsResponseAnalysisData: responseAnalysis,
    learnosityReportsScoringRubricResponseAnalysisData:
      scoringRubricResponseAnalysis,
    items: props.questions,
  };

  useEffect(() => {
    const hasCsperformance = pathname.includes('csperformance');
    // hasCsperformance and params.questionId is added to ensure
    // User is not redirected when viewing question details page
    if (isStudent && !params.questionId && !hasCsperformance) {
      router.push(
        `/${params.subject}/assessments/results/${params.assignmentId}/resources`,
      );
    }
  }, [isReady]);

  const { breadCrumbs, dispatch, determinePreviousPageName, ACTIONS } =
    useContext(BreadcrumbContext);
  useEffect(() => {
    const breadcrumbText = pathBreadCrumbMapping[route.name];
    const breadCrumbItem = breadCrumbs.find(
      (crumb) => crumb.text === breadcrumbText,
    );
    if (!breadCrumbItem && !isNil(breadcrumbText)) {
      const payload = {
        to: pathname,
        text: breadcrumbText,
      };
      dispatch({ type: ACTIONS.addBreadCrumb, payload });
    }
    !isNil(breadcrumbText) &&
      dispatch({
        type: ACTIONS.removeAdjacentBreadCrumbs,
        payload: breadcrumbText,
      });
  }, [route]);

  useEffect(() => {
    dispatch({
      type: ACTIONS.updatePreviousPageRoute,
      payload: {
        text: determinePreviousPageName(prevLocation),
        to: prevLocation,
      },
    });

    return () => dispatch({ type: ACTIONS.removeAllBreadCrumbs });
  }, [prevLocation]);

  return (
    <React.Fragment>
      <div id="Results" className="px-8.4">
        <ResultsContext.Provider value={resultsData}>
          <ResultsWrapper
            studentContext={{
              hasContext: false,
              studentId: null,
              resultsPath: '',
              questionsPath: '',
              progressPath: '',
            }}
            {...props}
            hasFrqQuestions={hasFrqQuestions}
          >
            {isReady ? (
              noResults ? (
                <div className="w-100 bg-gray-100 rounded-xl border-dashed border-gray-500 border leading-8 text-2xl px-5 py-4 mt-6">
                  No Results
                </div>
              ) : (
                React.cloneElement(props.children)
              )
            ) : (
              <LoadingSpinner />
            )}
          </ResultsWrapper>
        </ResultsContext.Provider>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  userData: state.userData,
  featureFlags: state.featureFlag,
  educationPeriod: state.filterControls.activeEducationPeriod,
  subjectData: state.subject,
  isPreApSubject: state.subject.data.isPreApSubject,
  classes: state.classes,
  studentsBySection: state.studentsBySection,
  prevLocation: localStorage.getItem('PREV_ROUTE'),
});

ResultsDataProvider.propTypes = {
  masterSubjectProgram: PropTypes.string,
  subjectInfo: PropTypes.object,
  questions: PropTypes.array,
};

ResultsDataProvider.defaultProps = {
  masterSubjectProgram: '',
  questions: [],
  subjectInfo: {},
  tagPairsData: {
    assignmentProgressWithTagPairs: {
      assessmentShortName: '',
      otherParts: [],
    },
  },
};

export default compose(
  connect(mapStateToProps),
  withRouter,
  withAdminInfo,
  withAssignment,
  withTrackResultsVisit,
  withSubmissionData,
  withSessionTemplate,
  CourseOutlineConfig,
  WithLearnosityData,
)(ResultsDataProvider);
